import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage, translations } from '../context/LanguageContext';

const { FiRefreshCw, FiQuote } = FiIcons;

const QuoteCard = () => {
  const { language } = useLanguage();
  const t = translations[language];
  
  const [currentQuote, setCurrentQuote] = useState(0);
  
  const quotes = {
    en: [
      {
        text: "The expert in anything was once a beginner.",
        author: "Helen Hayes"
      },
      {
        text: "Learning never exhausts the mind.",
        author: "Leonardo da Vinci"
      },
      {
        text: "The only way to do great work is to love what you do.",
        author: "Steve Jobs"
      },
      {
        text: "Success is not final, failure is not fatal: it is the courage to continue that counts.",
        author: "Winston Churchill"
      },
      {
        text: "The future belongs to those who believe in the beauty of their dreams.",
        author: "Eleanor Roosevelt"
      },
      {
        text: "It is during our darkest moments that we must focus to see the light.",
        author: "Aristotle"
      },
      {
        text: "The only impossible journey is the one you never begin.",
        author: "Tony Robbins"
      },
      {
        text: "In the middle of difficulty lies opportunity.",
        author: "Albert Einstein"
      }
    ],
    de: [
      {
        text: "Der Experte in allem war einmal ein Anfänger.",
        author: "Helen Hayes"
      },
      {
        text: "Lernen erschöpft den Geist nie.",
        author: "Leonardo da Vinci"
      },
      {
        text: "Der einzige Weg, großartige Arbeit zu leisten, ist zu lieben, was man tut.",
        author: "Steve Jobs"
      },
      {
        text: "Erfolg ist nicht endgültig, Misserfolg ist nicht fatal: Was zählt, ist der Mut weiterzumachen.",
        author: "Winston Churchill"
      },
      {
        text: "Die Zukunft gehört denen, die an die Schönheit ihrer Träume glauben.",
        author: "Eleanor Roosevelt"
      },
      {
        text: "In unseren dunkelsten Momenten müssen wir uns darauf konzentrieren, das Licht zu sehen.",
        author: "Aristoteles"
      },
      {
        text: "Die einzige unmögliche Reise ist die, die du nie beginnst.",
        author: "Tony Robbins"
      },
      {
        text: "In der Mitte der Schwierigkeit liegt die Möglichkeit.",
        author: "Albert Einstein"
      }
    ]
  };

  const currentLanguageQuotes = quotes[language];

  const nextQuote = () => {
    setCurrentQuote((prev) => (prev + 1) % currentLanguageQuotes.length);
  };

  useEffect(() => {
    const interval = setInterval(nextQuote, 30000); // Change quote every 30 seconds
    return () => clearInterval(interval);
  }, [currentLanguageQuotes.length]);

  // Reset quote index when language changes to prevent out of bounds
  useEffect(() => {
    setCurrentQuote(0);
  }, [language]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 backdrop-blur-md rounded-2xl p-6 border border-indigo-500/30"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <SafeIcon icon={FiQuote} className="text-2xl text-indigo-300 mr-3" />
          <h2 className="text-xl font-bold text-white">{t.dailyInspiration}</h2>
        </div>
        <button
          onClick={nextQuote}
          className="p-2 rounded-full bg-white/10 text-indigo-300 hover:bg-white/20 transition-all duration-200"
        >
          <SafeIcon icon={FiRefreshCw} className="text-lg" />
        </button>
      </div>

      <motion.div
        key={`${language}-${currentQuote}`}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <p className="text-white text-lg mb-4 italic leading-relaxed">
          "{currentLanguageQuotes[currentQuote].text}"
        </p>
        <p className="text-indigo-200 font-medium">
          — {currentLanguageQuotes[currentQuote].author}
        </p>
      </motion.div>

      <div className="flex justify-center mt-4 space-x-1">
        {currentLanguageQuotes.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentQuote(index)}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              index === currentQuote ? 'bg-indigo-400' : 'bg-white/30'
            }`}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default QuoteCard;